import React, { useState, useRef } from "react"
import styled from "styled-components"
import addToMailchimp from "gatsby-plugin-mailchimp"
import Img from "gatsby-image"
import { graphql, useStaticQuery, Link } from "gatsby"

import Divider from "../components/Divider"
import { H2, H3 } from "../components/Heading"
import BodyText from "../components/BodyText"
import { LAYOUT, FONT_FAMILIES, COLORS, LINKS } from "../constants"
import checkboxRest from "../assets/img/ourNextEvent/checkbox-rest.svg"
import checkboxSelected from "../assets/img/ourNextEvent/checkbox-selected.svg"

const SUBSCRIPTION_TYPES = {
  INDIVIDUAL: "INDIVIDUAL",
  TEAM: "TEAM",
}

const SUBSCRIPTION_TYPES_FORM_VALUE_MAP = {
  INDIVIDUAL: "Individual",
  TEAM: "Team",
}

const SUCCESS_FEEDBACK = {
  INDIVIDUAL: {
    heading: "Thank you for signing up to 5k May!",
    iconKey: "individual",
    body: (
      <>
        <p>
          Run, roll or walk 5k anytime during May, donate £5 to your chosen
          charity and upload a picture onto social! Remember to tag us so we can
          share!
        </p>
        <p>
          Ps. Once you've donated you'll unlock a goodie bag - it's our way of
          saying thank you!
        </p>
      </>
    ),
  },
  TEAM: {
    heading: "Thank you for signing up to 5k May!",
    iconKey: "team",
    body: (
      <>
        <p>
          Run, roll or walk 5k anytime during May, donate £5 to your chosen
          charity and upload a picture onto social! Remember to tag us so we can
          share!
        </p>
        <p>
          Ps. Once you've donated you'll unlock a goodie bag - it's our way of
          saying thank you!
        </p>
      </>
    ),
  },
}

const Root = styled.div`
  max-width: 906px;
  display: flex;
  margin: 0 auto;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 30px 0;
`

const Form = styled.form`
  width: 100%;
  padding: 20px 0;
  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;
  max-width: 580px;
  margin: 0 auto;
  @media (min-width: ${LAYOUT.BREAKPOINTS.MEDIUM}) {
    padding: 40px;
  }
`

const FormHead = styled.div`
  display: flex;
  justify-content: center;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
  width: 100%;
  flex-direction: column;
  align-items: center;
`

const FormHeading = styled(H2)`
  text-align: center;
  margin-bottom: 4px;
`

const Date = styled(H3)`
  font-family: ${FONT_FAMILIES.RFH_FINE};
  font-size: 2.6rem;
`

const FormHeadingCopy = styled(BodyText)`
  padding: 30px 20px;
`

const FormWrapper = styled.div`
  width: 100%;
  background: ${COLORS.WHITE};
  box-shadow: 0 0 24px 10px rgba(12, 33, 42, 0.16);
  padding: 54px 30px 20px 30px;

  @media (min-width: ${LAYOUT.BREAKPOINTS.MEDIUM}) {
    padding: 54px 65px 20px 65px;
  }
`

const Radio = styled.input`
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
`

interface IParticipantsImage {
  fluid: object
}

const ParticipantsImage = styled(Img)<IParticipantsImage>`
  width: 100%;
`

const RadioContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 0 auto;
  flex-wrap: wrap;
  padding-bottom: 24px;

  @media (min-width: ${LAYOUT.BREAKPOINTS.MEDIUM}) {
    flex-wrap: nowrap;
  }
`

const RadioHeading = styled(H3)`
  font-size: 1.6rem;
  margin-left: 8px;
`

const InputRow = styled.div`
  width: 100%;
`

const LastRow = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: column;
  @media (min-width: ${LAYOUT.BREAKPOINTS.MEDIUM}) {
    flex-direction: row;
  }
`

const Input = styled.input`
  width: 100%;
  height: 34px;
  font-size: 1.6rem;
  padding: 0 8px;
  border: 1px solid ${COLORS.GREY};
  border-radius: 2px;
  margin-bottom: 24px;
  color: ${COLORS.GREY};
`

const Label = styled.label`
  font-size: 1.6rem;
  color: ${COLORS.GREY};
  display: flex;
`

const SubscribeButton = styled.input`
  display: flex;
  align-items: center;
  padding: 0 30px;
  margin-top: 20px;
  line-height: normal;
  min-height: 40px;
  width: auto;
  background: ${COLORS.WHITE};
  border: 2px solid ${COLORS.GREY};
  color: ${COLORS.GREY};
  font-family: ${FONT_FAMILIES.RFH_REGULAR};
  font-size: 1.8rem;
  text-decoration: none;
  height: 40px;
  appearance: none;
  -moz-appearance: none;
  -webkit-appearance: none;

  &:disabled {
    opacity: 0.3;
  }

  @media (min-width: ${LAYOUT.BREAKPOINTS.MEDIUM}) {
    margin-top: 0;
  }
`

const SuccessFeedback = styled.div`
  max-width: 670px;
  border-radius: 11px;
  background: ${COLORS.GREY};
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 50px 30px;
  @media (min-width: ${LAYOUT.BREAKPOINTS.MEDIUM}) {
    padding: 55px 85px;
  }
`

const SuccessFeedbackHeading = styled(H2)`
  margin-bottom: 15px;

  line-height: 1.3;

  @media (min-width: ${LAYOUT.BREAKPOINTS.MEDIUM}) {
    margin-bottom: 15px;
  }
`

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  @media (min-width: ${LAYOUT.BREAKPOINTS.MEDIUM}) {
    margin-bottom: 0px;
  }
`

const ErrorText = styled(BodyText)`
  margin-top: 30px;
  p,
  a {
    color: ${COLORS.PINK};
  }
`

const Steps = styled.ul`
  list-style: inline;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 20px;

  @media (min-width: ${LAYOUT.BREAKPOINTS.MEDIUM}) {
    margin-bottom: 0;
  }
`

const Step = styled(H3)`
  font-size: 1.8rem;
`

const StepDivider = styled.li`
  height: 8px;
  width: 8px;
  border-radius: 100px;
  background-color: ${COLORS.PINK};
  margin: 0 5px;
  flex-shrink: 0;
  @media (min-width: ${LAYOUT.BREAKPOINTS.MEDIUM}) {
    margin: 0 30px;
  }
`

const CheckBoxImg = styled.img``

interface ISubscribeForm {
  rest?: object
}

const TermsCopy = styled(BodyText)`
  margin-top: 40px;
`

const OurNextEvent: React.FC<ISubscribeForm> = ({ ...rest }) => {
  const [firstName, setFirstName] = useState("")
  const [surname, setSurname] = useState("")
  const [email, setEmail] = useState("")
  const [organisation, setOrganisation] = useState("")
  const [subscriptionType, setSubscriptionType] = useState(
    SUBSCRIPTION_TYPES.INDIVIDUAL
  )
  const [responseIsError, setResponseIsError] = useState(null)
  const [responseIsSuccess, setResponseIsSuccess] = useState(null)
  const [response, setResponse] = useState("")
  const formEl = useRef(null)

  const handleSubmit = e => {
    e.preventDefault()

    addToMailchimp(
      email,
      {
        FNAME: firstName,
        LNAME: surname,
        SIGNUPAS: SUBSCRIPTION_TYPES_FORM_VALUE_MAP[subscriptionType],
        ORG: organisation,
      },
      null
    ).then(data => {
      setResponse(data.msg)
      if (data?.result === "error") {
        setResponseIsError(true)
      } else {
        setResponseIsSuccess(true)
        setResponseIsError(false)
        formEl.current.scrollIntoView({
          behavior: "smooth",
        })
      }
    })
  }

  const imageData = useStaticQuery(graphql`
    query OurNextEventQuery {
      participants: file(
        relativePath: { eq: "ourNextEvent/participants.png" }
      ) {
        childImageSharp {
          fluid(maxWidth: 776, pngQuality: 100, webpQuality: 100) {
            ...GatsbyImageSharpFluid_withWebp_noBase64
          }
        }
      }
    }
  `)

  const canSubmit = firstName && surname && email && subscriptionType
  return (
    <Root {...rest}>
      {responseIsSuccess ? (
        <SuccessFeedback ref={formEl}>
          <SuccessFeedbackHeading color={"WHITE"} alignment={"CENTER"}>
            {SUCCESS_FEEDBACK[subscriptionType].heading}
          </SuccessFeedbackHeading>

          <BodyText type={"LIGHT"} alignment={"CENTER"}>
            {SUCCESS_FEEDBACK[subscriptionType].body}
          </BodyText>
        </SuccessFeedback>
      ) : (
        <FormWrapper>
          <FormHead>
            <FormHeading>Our next event, 5k May</FormHeading>
            {/* <Date>1st-31st May 2021</Date> */}
            <FormHeadingCopy alignment={"CENTER"} type={"DARK"}>
              <p>
                One month of coming together for better! You can do your 5k
                anywhere, anytime, with anyone! You can run, roll, walk, cycle
                or even swim... It's all about getting out and feeling good
                while you're doing it - all whilst giving back to charity.
              </p>
              <p>
                <Link to={"/5k-may"}>Learn more about 5k May.</Link>
              </p>
            </FormHeadingCopy>
          </FormHead>
          <Divider />
          <Steps>
            <li>
              <Step>Participate</Step>
            </li>
            <StepDivider />
            <li>
              <Step>Donate</Step>
            </li>
            <StepDivider />
            <li>
              <Step>Nominate</Step>
            </li>
          </Steps>
          <ParticipantsImage
            loading={"eager"}
            fadeIn={false}
            fluid={imageData.participants.childImageSharp.fluid}
          />
          <Form>
            <RadioContainer>
              <RadioLabel>
                <Radio
                  type="radio"
                  name="type"
                  value="individual"
                  checked={subscriptionType === SUBSCRIPTION_TYPES.INDIVIDUAL}
                  onChange={() => {
                    setSubscriptionType(SUBSCRIPTION_TYPES.INDIVIDUAL)
                  }}
                />
                <CheckBoxImg
                  alt={
                    subscriptionType === SUBSCRIPTION_TYPES.INDIVIDUAL
                      ? "checkbox selected"
                      : "checkbox unselected"
                  }
                  src={
                    subscriptionType === SUBSCRIPTION_TYPES.INDIVIDUAL
                      ? checkboxSelected
                      : checkboxRest
                  }
                />
                <RadioHeading>
                  <p>Sign up as an individual</p>
                </RadioHeading>
              </RadioLabel>

              <RadioLabel>
                <Radio
                  type="radio"
                  name="type"
                  value="team"
                  checked={subscriptionType === SUBSCRIPTION_TYPES.TEAM}
                  onChange={() => {
                    setSubscriptionType(SUBSCRIPTION_TYPES.TEAM)
                  }}
                />
                <CheckBoxImg
                  alt={
                    subscriptionType === SUBSCRIPTION_TYPES.INDIVIDUAL
                      ? "checkbox selected"
                      : "checkbox unselected"
                  }
                  src={
                    subscriptionType === SUBSCRIPTION_TYPES.TEAM
                      ? checkboxSelected
                      : checkboxRest
                  }
                />
                <RadioHeading>
                  <p>Sign up as a team</p>
                </RadioHeading>
              </RadioLabel>
            </RadioContainer>
            <Divider />

            {subscriptionType && (
              <>
                <InputRow>
                  <Label htmlFor={"firstName"}>First name</Label>
                  <Input
                    id={"firstName"}
                    name={"firstName"}
                    type={"text"}
                    onChange={e => setFirstName(e.target.value)}
                    value={firstName}
                  />
                </InputRow>
                <InputRow>
                  <Label htmlFor="surname">Surname</Label>
                  <Input
                    id={"surname"}
                    name={"surname"}
                    type={"text"}
                    onChange={e => setSurname(e.target.value)}
                    value={surname}
                  />
                </InputRow>
                <InputRow>
                  <Label htmlFor="email">Email address</Label>
                  <Input
                    id={"email"}
                    name={"email"}
                    type={"email"}
                    onChange={e => setEmail(e.target.value)}
                    value={email}
                  />
                </InputRow>
                <InputRow>
                  <Label htmlFor="organisation">Organisation</Label>
                  <Input
                    id={"organisation"}
                    name={"organisation"}
                    type={"text"}
                    onChange={e => setOrganisation(e.target.value)}
                    value={organisation}
                  />
                </InputRow>
                <LastRow>
                  <BodyText type={"DARK"}>
                    <p>
                      Already participated?{" "}
                      <a
                        id={"donate-next-event"}
                        href={LINKS.DONATE}
                        target={"_blank"}
                        rel={"noopener"}
                      >
                        Donate now
                      </a>
                    </p>
                  </BodyText>
                  <SubscribeButton
                    id={"next-event-sign-up"}
                    disabled={!canSubmit}
                    type={"submit"}
                    onClick={handleSubmit}
                    value={"Sign up"}
                  />
                </LastRow>
                {responseIsError && (
                  <ErrorText type={"DARK"} alignment={"CENTER"}>
                    <p dangerouslySetInnerHTML={{ __html: response }} />
                  </ErrorText>
                )}
                <TermsCopy type={"DARK"} alignment={"CENTER"}>
                  <p>
                    By registering your interest you are consenting to be
                    contacted by Run for Heroes in relation to our next
                    campaign, any upcoming events, announcements, news and other
                    marketing material.
                  </p>
                </TermsCopy>
              </>
            )}
          </Form>
        </FormWrapper>
      )}
    </Root>
  )
}

export default OurNextEvent
